import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { AppDispatch, RootState, useAppDispatch, useAppSelector } from '../../../logic/store/Store';
import { setTreatmentsExpanded, setTreatmentSort } from '../../../logic/store/UI/UISlice';
import { ICustomTreatmentForUi } from '../../../logic/Models/Responses/CustomTreatmentResponse';
import { dynamicSort, makeDispatch } from '../../../logic/Utility/Utils';
import styled, { useTheme } from 'styled-components';
import { Button } from '../../../components/Button/Button';
import { ReactComponent as PlusIcon } from '../../../assets/images/PlusIcon.svg';
import { StyledModal } from '../../../components/StyledModal/StyledModal';
import { Modal } from 'antd';
import { ReactComponent as SoyIcon } from '../../FieldActivities/Icons/SoyIcon.svg';
import { DummyIdPrefix } from '../../../logic/store/User/CustomTreatmentsActions';
import { ICustomTreatmentRequest } from '../../../logic/Models/Requests/CustomTreatmentRequest';
import { Dropdown } from '../../../components/Dropdown/Dropdown';
import { getCurrentActingUser } from '../../../logic/store/User/AuthSlice';
import { CustomTreatmentRow } from './TreatmentRow';
import _ from 'lodash';
import { IOptionItem } from '../../../components/Dropdown/IOptionItem';
import { useAsyncThunkListener } from '../../../logic/Utility/useAsyncThunkListener';
import { getSyngentaTreatments, getUserDefaultTreatment, getCustomTreatments, createCustomTreatment, deleteCustomTreatment, updateCustomTreatment, setUserDefaultTreatment } from '../../../logic/store/Seeds/TreatmentActions';
import { v4 } from 'uuid';
import { CoveringLoader } from '../../../components/LoadingSpinner/CoveringLoader';
import { ScaleLoader } from 'react-spinners';

const StyledHeaderButton = styled(Button)`
	margin-left: 10px;
	padding-left: 15px;
	padding-right: 15px;
`;

const mapStateToProps = (state: RootState) => ({
	CurrentActingUser: getCurrentActingUser(state),
	TreatmentSort: state.ui.TreatmentSort,
	CustomTreatments: state.customTreatments.treatments,
});

const mapDispatchToProps = (dispatch: AppDispatch) =>
{
	return {
		GetUserDefaultTreatment: makeDispatch(dispatch, getUserDefaultTreatment),
		GetCustomTreatments: makeDispatch(dispatch, getCustomTreatments),
		SetExpanded: makeDispatch(dispatch, setTreatmentsExpanded),
		SetTreatmentSort: makeDispatch(dispatch, setTreatmentSort),
	};
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface ISoybeanTreatmentsProps extends PropsFromRedux
{

}

const StyledSoyIcon = styled(SoyIcon)<{ fillcolor: string }>`
	path {
		stroke: ${props => props.fillcolor};
	}
`;

// Custom treatments for soybean CRUD ui
const SoybeanTreatmentsComponent = (props: ISoybeanTreatmentsProps) =>
{
	const {
		CurrentActingUser,
		TreatmentSort,
		SetExpanded,
	} = props;

	const theme = useTheme();

	const [treatments, setTreatments] = useState<ICustomTreatmentForUi[]>([]);
	const [displayResetModal, setDisplayResetModal] = useState(false);
	// Delete confirmation modal state - needed because backend now automatically converts 
	// existing proposals/carts to "Untreated" when custom treatments are deleted
	const [displayDeleteModal, setDisplayDeleteModal] = useState(false);
	const [displaySaveConfirmModal, setDisplaySaveConfirmModal] = useState(false);
	const [treatmentToDelete, setTreatmentToDelete] = useState<number | null>(null);
	const [hasChanges, setHasChanges] = useState(false);
	const [modifiedTreatments, setModifiedTreatments] = useState<{ [id: string]: ICustomTreatmentRequest }>({});
	const [selectedDefaultTreatment, setSelectedDefaultTreatment] = useState<string>(undefined);
	const [isSaving, setIsSaving] = useState(false);

	const syngentaTreatments = useAsyncThunkListener(getSyngentaTreatments);
	const customTreatments = useAsyncThunkListener(getCustomTreatments);
	const defaultTreatments = useAsyncThunkListener(getUserDefaultTreatment);

	const currentGrowerId = useAppSelector(state => state.ui.SelectedGrowerId);

	const dispatch = useAppDispatch();
	useEffect(() => 
	{
		dispatch(getSyngentaTreatments());
		dispatch(getCustomTreatments());
		dispatch(getUserDefaultTreatment());
	}, [dispatch]);

	useEffect(() => 
	{
		if(!customTreatments.data)
		{
			return;
		}

		setTreatments(customTreatments.data?.map<ICustomTreatmentForUi>(ct => ({
			...ct,
			IsNew: false,
			ErrorMessage: undefined,
		})) ?? []);
	}, [customTreatments.data]);

	// load all relevant data
	const defaultTreatmentOptions = useMemo(() => 
	{
		const options:IOptionItem[] = [];
		if (syngentaTreatments.data?.length)
		{
			const syngentaOptions = syngentaTreatments.data
				.map(t => ({ label: t.Description, value: t.Id } as IOptionItem));
			options.push(...syngentaOptions);
		}

		if (customTreatments.data?.length)
		{
			const customOptions = customTreatments.data
				.map(t => ({ label: t.Name, value: t.Id, disabled: !!t.IsDeleted } as IOptionItem));
			options.push(...customOptions);
		}

		if (options.length > 0)
		{
			options.sort(dynamicSort('label'));
		}
		return options;
	}, [customTreatments.data, syngentaTreatments.data]);

	const addTreatment = useCallback(() =>
	{
		const Id = DummyIdPrefix + v4();
		const newTreatment: ICustomTreatmentForUi = {
			Id,
			IsDeleted: false,
			Created: new Date().toISOString(),
			Name: '',
			Notes: '',
			PricePerBag: undefined,
			IsNew: true,
			ErrorMessage: undefined,
		};
		setTreatments((prev) => [ ...prev, newTreatment]);
		SetExpanded({ [Id]: true });
		setHasChanges(true);
	}, [SetExpanded]);

	const isLoading = isSaving || syngentaTreatments.isLoading || customTreatments.isLoading || defaultTreatments.isLoading;

	const onSave = async () => 
	{
		if (!hasChanges || isLoading)
		{
			return;
		}

		setIsSaving(true);

		// Update all modified treatments
		// The only reason this is one giant statement is to ensure that the return types are inferred correctly
		const tasks = 
			Object.values(modifiedTreatments).filter(t => t.Id.startsWith(DummyIdPrefix))
				.map(t => dispatch(createCustomTreatment(t)))
				.concat(Object.values(modifiedTreatments).filter(t => !t.Id.startsWith(DummyIdPrefix) && !t.IsDeleted)
					.map(t => dispatch(updateCustomTreatment(t))))
				.concat(Object.values(modifiedTreatments).filter(t => !t.Id.startsWith(DummyIdPrefix) && t.IsDeleted)
					.map(t => dispatch(deleteCustomTreatment(t))));
		
		// Update the default treatment
		const setDefaultResult = await dispatch(setUserDefaultTreatment(selectedDefaultTreatment));
		if (!setDefaultResult || setDefaultResult.meta.requestStatus === 'rejected')
		{
			console.error('Failed to set user default treatment:', setDefaultResult);
		}

		let hadErrors = false;
		if(tasks.length)
		{
			const results = await Promise.all(tasks);
			const failedResults = results.filter(r => r.meta.requestStatus === 'rejected');
			if(failedResults.length > 0)
			{
				hadErrors = true;
				for(const failedResult of failedResults)
				{
					setTreatments((prev) => prev.map(t => 
					{
						if(t.Id === failedResult.meta.arg.Id)
						{
							return { ...t, ErrorMessage: 'ERROR: ' + failedResult.payload as string };
						}
						return t;
					}));
				}
			}
		}

		// Re-fetch the latest data
		if(!hadErrors)
		{
			await dispatch(getUserDefaultTreatment());
			await dispatch(getCustomTreatments());
			setHasChanges(false);
			setModifiedTreatments({});
		}

		setIsSaving(false);
	};

	const onReset = () =>
	{
		if (displayResetModal)
		{
			const exec = async () =>
			{
				setIsSaving(true);
				// We don't await the first two fetches of data so they will run concurrently.
				dispatch(getSyngentaTreatments());
				dispatch(getCustomTreatments());
				await dispatch(getUserDefaultTreatment());
				setModifiedTreatments({});
				setHasChanges(false);
				setIsSaving(false);
			};
			exec();
		}
		setDisplayResetModal(false);
	};

	const onSetUserDefaultTreatment = (treatmentName: string) =>
	{
		setHasChanges(true);
		setSelectedDefaultTreatment(treatmentName);
	};


	// When a default is loaded, set it
	useEffect(() =>
	{
		if (defaultTreatments.data)
		{
			setSelectedDefaultTreatment(defaultTreatments.data);
		}
	}, [defaultTreatments.data]);

	// When the user toggles a Treatment Sort, re-sort the treatments
	useEffect(() =>
	{
		const { field, sortDirection } = TreatmentSort;
		if (sortDirection !== 'none')
		{
			setTreatments((prev) => [...prev].sort(dynamicSort(field, sortDirection)));
		}
	}, [TreatmentSort]);


	const onDelete = (index: number) =>
	{
		const treatmentToDelete = treatments[index];

		if (!treatmentToDelete)
		{
			return;
		}

		// New treatments have no backend impact - safe to delete immediately without confirmation
		if (treatmentToDelete.Id.startsWith(DummyIdPrefix))
		{
			setModifiedTreatments((prev) => 
			{ 
				const next = {...prev};
				delete next[treatmentToDelete.Id];
				return next; 
			});

			setTreatments((prev) => prev.filter((t) => t.Id !== treatmentToDelete.Id));
		}
		// Allow users to restore accidentally deleted treatments before saving
		else if (treatmentToDelete.IsDeleted)
		{
			treatmentToDelete.IsDeleted = false;
			setTreatments((prev) => [ ...prev.map((t, i) => i == index ? treatmentToDelete : t)]);
			setModifiedTreatments((prev) => ({...prev, [treatmentToDelete.Id]: treatmentToDelete}));
			setHasChanges(true);
		}
		// Saved treatments require confirmation due to backend impact on existing proposals/carts
		else
		{
			setTreatmentToDelete(index);
			setDisplayDeleteModal(true);
		}
	};

	const onCancelDelete = () =>
	{
		setDisplayDeleteModal(false);
		setTreatmentToDelete(null);
	};

	const openConfirmSaveModal = () =>
	{
		setDisplaySaveConfirmModal(true);
	};

	const onCancelSave = () =>
	{
		setDisplaySaveConfirmModal(false);
	};

	const onConfirmSave = () =>
	{
		setDisplaySaveConfirmModal(false);
		onSave();
	};

	// Execute the deletion after user confirms understanding of backend impact
	const onConfirmDelete = () =>
	{
		if (treatmentToDelete === null) return;

		const treatmentToDeleteObj = treatments[treatmentToDelete];
		treatmentToDeleteObj.IsDeleted = true;
		
		setTreatments((prev) => [ ...prev.map((t, i) => i === treatmentToDelete ? treatmentToDeleteObj : t)]);
		setModifiedTreatments((prev) => ({...prev, [treatmentToDeleteObj.Id]: treatmentToDeleteObj}));
		setHasChanges(true);
		
		setDisplayDeleteModal(false);
		setTreatmentToDelete(null);
	};

	const onNameChange = useCallback((index: number, value: string) =>
	{
		const treatmentToUpdate = { ...treatments[index], Name: value };
		treatmentToUpdate.ErrorMessage = undefined;

		// Check to see if the name is identical to any existing treatments
		const existingTreatments = treatments.filter(t => t.Id !== treatmentToUpdate.Id && !t.IsDeleted && t.Name === value);
		if(existingTreatments.length > 0)
		{
			treatmentToUpdate.ErrorMessage = 'Name cannot be the same as a non-deleted existing treatment. Please delete the existing treatment to re-create a new treatment with the same name.';
		}

		setTreatments((prev) => [ ...prev.map((t, i) => i == index ? treatmentToUpdate : t)]);
		setModifiedTreatments((prev) => ({...prev, [treatmentToUpdate.Id]: treatmentToUpdate}));
		setHasChanges(true);
	}, [treatments]);

	const onPriceChange = useCallback((index: number, value: number) =>
	{
		const treatmentToUpdate = { ...treatments[index], PricePerBag: value };
		setTreatments((prev) => [ ...prev.map((t, i) => i == index ? treatmentToUpdate : t)]);
		setModifiedTreatments((prev) => ({...prev, [treatmentToUpdate.Id]: treatmentToUpdate}));
		setHasChanges(true);
	}, [treatments]);

	const onNotesChange = useCallback((index: number, value: string) =>
	{
		const treatmentToUpdate = { ...treatments[index], Notes: value };
		setTreatments((prev) => [ ...prev.map((t, i) => i == index ? treatmentToUpdate : t)]);
		setModifiedTreatments((prev) => ({...prev, [treatmentToUpdate.Id]: treatmentToUpdate}));
		setHasChanges(true);
	}, [treatments]);

	return (
		<div
			className='Container'
			style={{
				display: 'flex',
				flexDirection: 'column',
				flex: '1 1 auto',
				marginTop: 15,
				paddingBottom: 20,
				height: '100%',
				width: '100%',
			}}
		>
			<div className='SoybeanTreatment-Header' 
				style={{ 
					paddingTop: 20, 
					display: 'flex', 
					flexWrap: 'wrap',
					alignItems: 'flex-end',
					gap: 10,
					paddingBottom: 10,
				}}>
				<div style={{ 
					fontSize: `${theme.fontSizes.xLarge}`,
					fontWeight: theme.fontWeights.bold,
					textWrap: 'balance',
				}}>
					Soybean Treatments for {CurrentActingUser?.UserName ? CurrentActingUser?.UserName : CurrentActingUser?.UserEmail}
				</div>
				<div style={{ 
					display: 'flex', 
					flexWrap: 'wrap', 
					marginLeft: 'auto', 
					gap: 10,
					minWidth: 'fit-content', 

				}}>
					<StyledHeaderButton
						variant='outlined'
						style={{ 
							justifyContent: 'center', 
							alignItems: 'center', 
							display: 'flex', 
							minWidth: 'fit-content', 
							marginLeft: 0, 
							flex: 1,
						}}
						onClick={() => addTreatment()}
					>
						<PlusIcon fill={theme.colors.darkGrey} style={{ marginRight: 10 }} />
						<span>Add a treatment</span>
					</StyledHeaderButton>
					<StyledHeaderButton
						className='Reset_Button'
						variant='outlined'
						style={{
							minWidth: 'fit-content', 
							marginLeft: 0, 
							flex: 1,
						}}
						onClick={() => setDisplayResetModal(true)}
						disabled={!hasChanges}
					>
						Reset
					</StyledHeaderButton>
					<StyledHeaderButton
						className='Save_Button'
						variant='outlined'
						style={{
							minWidth: 'fit-content', 
							marginLeft: 0, 
							flex: 1,
						}}
						onClick={currentGrowerId ? openConfirmSaveModal : onSave}
						disabled={!hasChanges || isLoading}
					>
						Save
					</StyledHeaderButton>
				</div>
				<StyledModal
					title='Are you sure you want to discard changes?'
					open={displayResetModal}
					onCancel={() => setDisplayResetModal(false)}
				>
					<Button onClick={onReset} variant='outlined' style={{ marginRight: 10 }}>Reset</Button>
					<Button onClick={() => setDisplayResetModal(false)} variant='outlined'>Cancel</Button>
				</StyledModal>
				<Modal
					open={displayDeleteModal}
					onCancel={onCancelDelete}
					title={
						<div style={{
							display: 'flex',
							flexDirection: 'row',
							alignItems: 'center'
						}}>
							<StyledSoyIcon style={{ marginRight: 16 }} fillcolor={theme.colors.primary}/>
							<div
								style={{
									fontSize: theme.fontSizes.large,
									fontWeight: 700,
									position: 'relative',
									top: 1
								}}
							>
								Delete Custom Treatment?
							</div>
						</div>
					}
					className='DeleteTreatmentModal'
					closable={true}
					footer={
						<div>
							<Button
								variant='outlined'
								style={{ marginLeft: 8, paddingLeft: 16, paddingRight: 16 }}
								onClick={onCancelDelete}
								className='cancelButton'
							>
								Cancel
							</Button>
							<Button
								variant='main'
								style={{  marginLeft: 8, paddingLeft: 16, paddingRight: 16 }}
								onClick={onConfirmDelete}
								className='continueButton'
							>
								Continue
							</Button>
						</div>}
					centered
				>
					<div style={{ fontSize: theme.fontSizes.small, display: 'flex', flexDirection: 'column' }}>
						<span>
							{'When a custom treatment is deleted, all existing Acre Proposals, Field Proposals, and Shopping Carts ' + 
							'will automatically convert products with this custom treatment to be "Untreated".'}
						</span>
					</div>
					<div style={{ fontWeight: theme.fontWeights.bold, marginTop: 8 }}>
						Do you wish to continue?
					</div>
				</Modal>
				<Modal
					open={displaySaveConfirmModal}
					onCancel={onCancelSave}
					title={
						<div style={{
							display: 'flex',
							flexDirection: 'row',
							alignItems: 'center'
						}}>
							<StyledSoyIcon style={{ marginRight: 16 }} fillcolor={theme.colors.primary}/>
							<div
								style={{
									fontSize: theme.fontSizes.large,
									fontWeight: 700,
									position: 'relative',
									top: 1
								}}
							>
								Save Custom Treatments?
							</div>
						</div>
					}
					className='SaveTreatmentModal'
					closable={true}
					footer={
						<div>
							<Button
								variant='outlined'
								style={{ marginLeft: 8, paddingLeft: 16, paddingRight: 16 }}
								onClick={onCancelSave}
								className='cancelButton'
							>
								Cancel
							</Button>
							<Button
								variant='main'
								style={{  marginLeft: 8, paddingLeft: 16, paddingRight: 16 }}
								onClick={onConfirmSave}
								className='continueButton'
							>
								Continue
							</Button>
						</div>}
					centered
				>
					<div style={{ fontSize: theme.fontSizes.small, display: 'flex', flexDirection: 'column' }}>
						<span>
							{'Any changes to your custom treatments may impact products in active plans. To see the updates you have made, please ' +
							'navigate to the Dashboard and re-select your grower.'}
						</span>
					</div>
					<div style={{ fontWeight: theme.fontWeights.bold, marginTop: 8 }}>
						Do you wish to continue?
					</div>
				</Modal>
			</div>
			<div style={{ 
				display: 'flex', 
				columnGap: 10,
				marginBottom: 10,
				flexWrap: 'wrap',
			}}>
				<div style={{ 
					fontSize: `${theme.fontSizes.normal}`, 
					fontWeight: theme.fontWeights.bold, 
					alignSelf: 'center',
					minWidth: 'min-content',
				}}>
					Default Treatment:
				</div>
				{
					!isLoading && (
						<div className='SoybeanTreatment-DefaultSelect'
							style={{ 
								maxWidth: '100%',
							}}
						>
							<Dropdown
								variant='grey'
								options={defaultTreatmentOptions}
								placeholder={'Select a default treatment'}
								selected={selectedDefaultTreatment}
								onSelect={onSetUserDefaultTreatment}
							/>
						</div>
					)
				}
			</div>
			<div style={{ 
				marginBottom: 10, 
				fontSize: theme.fontSizes.extraSmall,
				textWrap: 'pretty',
			}}>
				**Treatment Name and Price Per Bag are required for new entries. Treatment Name cannot be the same as any other non-deleted entries.
			</div>
			{
				(!!treatments.length) && 
				<div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', overflow: 'hidden' }}>
					<div
						style={{
							backgroundColor: 'black',
							color: 'white',
							fontWeight: theme.fontWeights.bold,
							display: 'flex',
							flexDirection: 'row',
							fontSize: 12,
							borderTopLeftRadius: 6,
							borderTopRightRadius: 6,
							paddingLeft: 10,
							paddingRight: 10,
							paddingTop: 5,
							paddingBottom: 5,
						}}>
						<div style={{ width: '30%', textAlign: 'left' }}>Name</div>
						<div style={{ width: '15%', textAlign: 'left' }}>Price Per Bag</div>
						<div style={{ width: '40%', textAlign: 'left' }}>Date Created</div>
					</div>
					<div style={{ display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
						{
							treatments.map((t, index) =>
								<CustomTreatmentRow
									treatment={t}
									index={index}
									key={`${t.Id}-${index}`}
									onDelete={onDelete}
									onNameChange={onNameChange}
									onPriceChange={onPriceChange}
									onNotesChange={onNotesChange}
								/>
							)
						}
					</div>
				</div>
			}
			<CoveringLoader className={isLoading ? 'loading' : ''}>
				<ScaleLoader color={theme.colors.primary} loading={isLoading} />
			</CoveringLoader>
		</div >
	);
};

export const SoybeanTreatments = connector(SoybeanTreatmentsComponent);
